import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    UserGroupIcon,
    MapIcon,
    ChartBarIcon,
    ScaleIcon,
    TruckIcon,
    CurrencyDollarIcon,
    AcademicCapIcon,
    TrashIcon,
    BanknotesIcon,
    PlusIcon
} from '@heroicons/react/24/outline';

import KPICard from '../components/KPICard';
import DataTable from '../components/DataTable';
import FilterPanel from '../components/FilterPanel';
import ExportButton from '../components/ExportButton';
import StatusBadge from '../components/StatusBadge';
import ModalLayout from '../components/ModalLayout';
import CreateCohortForm from '../components/cohort/CreateCohortForm';
import MapboxMiniMap from '../components/maps/MapboxMiniMap';
import { apiGet, apiPost, apiPatch, apiDelete, getAuthToken, subscribeToDataInvalidation } from '../utils/api';
import { formatCurrency } from '../utils/formatters';



// AssignFarmerButton Component
interface AssignFarmerButtonProps {
    cohortId: number;
    onAssignmentUpdate: () => void;
}

const AssignFarmerButton: React.FC<AssignFarmerButtonProps> = ({ cohortId, onAssignmentUpdate }) => {
    const [availableFarmers, setAvailableFarmers] = useState<any[]>([]);
    const [selectedFarmers, setSelectedFarmers] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const fetchAvailableFarmers = async () => {
        try {
            const data = await apiGet('/api/farmers/unassigned');
            setAvailableFarmers(data as any[]);
        } catch (error) {
            console.error('Failed to fetch available farmers:', error);
        }
    };

    const handleAssignFarmers = async () => {
        if (selectedFarmers.length === 0) {
            alert('Please select at least one farmer');
            return;
        }

        setLoading(true);
        try {
            await apiPost(`/api/cohorts/${cohortId}/assign-farmers`, {
                farmer_ids: selectedFarmers
            });
            
            setShowModal(false);
            setSelectedFarmers([]);
            onAssignmentUpdate();
            alert('Farmers assigned successfully!');
        } catch (error) {
            console.error('Failed to assign farmers:', error);
            alert('Failed to assign farmers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleFarmerSelection = (farmerId: number) => {
        setSelectedFarmers(prev => 
            prev.includes(farmerId) 
                ? prev.filter(id => id !== farmerId)
                : [...prev, farmerId]
        );
    };

    return (
        <>
            <button
                onClick={() => {
                    setShowModal(true);
                    fetchAvailableFarmers();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
                Assign Farmers
            </button>

            {showModal && (
                <ModalLayout
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    title="Assign Farmers to Cohort"
                    size="lg"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <h4 className="font-medium text-gray-900 mb-2">Available Farmers ({availableFarmers.length})</h4>
                            <p className="text-sm text-gray-600">Select farmers to assign to this cohort</p>
                        </div>

                        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                            {availableFarmers.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">
                                    No unassigned farmers available
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {availableFarmers.map((farmer) => (
                                        <div key={farmer.id} className="p-4 hover:bg-gray-50">
                                            <label className="flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFarmers.includes(farmer.id)}
                                                    onChange={() => toggleFarmerSelection(farmer.id)}
                                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <div className="flex items-center flex-1">
                                                    <img
                                                        src={farmer.photo_url || 'https://via.placeholder.com/40'}
                                                        alt={farmer.full_name}
                                                        className="w-10 h-10 rounded-full object-cover border border-gray-200 mr-3"
                                                        onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40?text=User'; }}
                                                    />
                                                    <div>
                                                        <p className="font-medium text-gray-900">{farmer.full_name}</p>
                                                        <p className="text-sm text-gray-500">{farmer.phone} • {farmer.location}</p>
                                                        <p className="text-xs text-gray-400">{farmer.plot_size_hectares} ha plot</p>
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-between items-center">
                            <p className="text-sm text-gray-600">
                                {selectedFarmers.length} farmer{selectedFarmers.length !== 1 ? 's' : ''} selected
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAssignFarmers}
                                    disabled={loading || selectedFarmers.length === 0}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Assigning...' : `Assign ${selectedFarmers.length} Farmer${selectedFarmers.length !== 1 ? 's' : ''}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </ModalLayout>
            )}
        </>
    );
};
interface AssignAgronomistSectionProps {
    cohortId: number;
    currentAgronomist?: string;
    onAssignmentUpdate: () => void;
}

const AssignAgronomistSection: React.FC<AssignAgronomistSectionProps> = ({ 
    cohortId, 
    currentAgronomist, 
    onAssignmentUpdate 
}) => {
    const [agronomists, setAgronomists] = useState<any[]>([]);
    const [selectedAgronomist, setSelectedAgronomist] = useState('');
    const [loading, setLoading] = useState(false);
    const [showAssignForm, setShowAssignForm] = useState(false);

    useEffect(() => {
        fetchAgronomists();
    }, []);

    const fetchAgronomists = async () => {
        try {
            const data = await apiGet('/api/users?role=agronomist');
            setAgronomists(data as any[]);
        } catch (error) {
            console.error('Failed to fetch agronomists:', error);
        }
    };

    const handleAssignAgronomist = async () => {
        if (!selectedAgronomist) {
            alert('Please select an agronomist');
            return;
        }

        setLoading(true);
        try {
            await apiPost(`/api/cohorts/${cohortId}/assign-agronomist`, {
                agronomist_id: selectedAgronomist
            });
            
            setShowAssignForm(false);
            setSelectedAgronomist('');
            onAssignmentUpdate();
            alert('Agronomist assigned successfully!');
        } catch (error) {
            console.error('Failed to assign agronomist:', error);
            alert('Failed to assign agronomist. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
            <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-900">Agronomist Assignment</h4>
                {!showAssignForm && (
                    <button
                        onClick={() => setShowAssignForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                        {currentAgronomist ? 'Reassign' : 'Assign'} Agronomist
                    </button>
                )}
            </div>

            {currentAgronomist && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800">
                        <span className="font-medium">Current Agronomist:</span> {currentAgronomist}
                    </p>
                </div>
            )}

            {showAssignForm && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Select Agronomist
                        </label>
                        <select
                            value={selectedAgronomist}
                            onChange={(e) => setSelectedAgronomist(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Choose an agronomist...</option>
                            {agronomists.map((agronomist) => (
                                <option key={agronomist.id} value={agronomist.id}>
                                    {agronomist.full_name} ({agronomist.email})
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="flex gap-3">
                        <button
                            onClick={handleAssignAgronomist}
                            disabled={loading || !selectedAgronomist}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                        >
                            {loading ? 'Assigning...' : 'Assign'}
                        </button>
                        <button
                            onClick={() => {
                                setShowAssignForm(false);
                                setSelectedAgronomist('');
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};


interface Cohort {
    id: number;
    name: string;
    croppingSystem: 'Avocado' | 'Macadamia';
    location: string;
    farmerCount: number;
    areaHa: number;
    areaAcres?: number;
    activeFarmerCount?: number;
    retentionRate?: number;
    status: 'Active' | 'Warning' | 'At Risk';
    vslaGroup: string;
    agronomist_name?: string;
    performance: {
        yieldIncrease: number;
        yieldTrend?: number;
        yieldTarget?: number;
        repaymentRate: number;
        attendanceRate: number;
        avgIncome: number;
        postHarvestLoss?: number;
        vslaScore?: number;
    };
}

const CohortsPage: React.FC = () => {
    const { user } = useAuth();
    const canManageCohorts = user?.role === 'project_manager' || user?.role === 'agronomist';

    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
    const [loading, setLoading] = useState(true);
    const [cohortMetrics, setCohortMetrics] = useState<any>(null);
    const [cohortFarmers, setCohortFarmers] = useState<any[]>([]);
    const [agronomyData, setAgronomyData] = useState<any>(null);
    const [financialData, setFinancialData] = useState<any>(null);
    const [trainingData, setTrainingData] = useState<any>(null);
    const [logisticsData, setLogisticsData] = useState<any>(null);
    const [tabLoading, setTabLoading] = useState<string | null>(null);
    const [systemFilter, setSystemFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [locationFilter, setLocationFilter] = useState('all');
    const [retentionFilter, setRetentionFilter] = useState('all');
    const [sortBy, setSortBy] = useState<'name' | 'farmers' | 'area' | 'retention'>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [activeTab, setActiveTab] = useState<'overview' | 'farmers' | 'farm-map' | 'agronomy' | 'financials' | 'training' | 'logistics'>('overview');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingCohort, setEditingCohort] = useState<Cohort | null>(null);

    useEffect(() => {
        fetchCohorts();
    }, []);

    useEffect(() => {
        if (selectedCohort) {
            fetchCohortDetails(selectedCohort.id);
        }
    }, [selectedCohort]);

    const loadFallbackData = () => {
        const fallbackData: Cohort[] = [
            { id: 1, name: 'Huye Avocado Group A', croppingSystem: 'Avocado', location: 'Huye', farmerCount: 25, activeFarmerCount: 24, retentionRate: 96, areaHa: 2.5, status: 'Active', vslaGroup: 'VSLA-Huye-01', performance: { yieldIncrease: 28, repaymentRate: 95, attendanceRate: 92, avgIncome: 45000 } },
            { id: 2, name: 'Nyanza Macadamia Co-op', croppingSystem: 'Macadamia', location: 'Nyanza', farmerCount: 30, activeFarmerCount: 27, retentionRate: 90, areaHa: 3.2, status: 'Active', vslaGroup: 'VSLA-Nyanza-02', performance: { yieldIncrease: 22, repaymentRate: 88, attendanceRate: 85, avgIncome: 38000 } },
            { id: 3, name: 'Gisagara Mix Farm', croppingSystem: 'Avocado', location: 'Gisagara', farmerCount: 20, activeFarmerCount: 16, retentionRate: 80, areaHa: 1.8, status: 'Warning', vslaGroup: 'VSLA-Gisa-03', performance: { yieldIncrease: 15, repaymentRate: 75, attendanceRate: 80, avgIncome: 32000 } },
            { id: 4, name: 'Ruhango Legume Hub', croppingSystem: 'Macadamia', location: 'Ruhango', farmerCount: 28, activeFarmerCount: 26, retentionRate: 93, areaHa: 3.0, status: 'Active', vslaGroup: 'VSLA-Ruhango-04', performance: { yieldIncrease: 30, repaymentRate: 94, attendanceRate: 90, avgIncome: 41000 } },
        ];
        setCohorts(fallbackData);
    };

    const fetchCohorts = async () => {
        setLoading(true);
        try {
            const data = await apiGet<Cohort[]>('/api/cohorts');
            const formattedData = data.map((c: any) => ({
                areaHa: Number(c.target_area ?? c.total_area_hectares ?? c.areaHa ?? c.area_hectares ?? 0),
                areaAcres: Number(c.total_area_acres ?? c.areaAcres ?? c.area_acres ?? ((c.target_area ?? c.total_area_hectares ?? c.areaHa ?? c.area_hectares ?? 0) * 2.47105)),
                farmerCount: Number(c.farmerCount ?? c.farmer_count ?? 0),
                activeFarmerCount: Number(c.activeFarmerCount ?? c.active_farmer_count ?? c.farmerCount ?? c.farmer_count ?? 0),
                retentionRate: Number(
                    c.retentionRate
                    ?? c.retention_rate
                    ?? ((Number(c.farmerCount ?? c.farmer_count ?? 0) > 0)
                        ? (Number(c.activeFarmerCount ?? c.active_farmer_count ?? c.farmerCount ?? c.farmer_count) / Number(c.farmerCount ?? c.farmer_count)) * 100
                        : 0)
                ),
                id: c.id,
                name: c.name,
                croppingSystem: c.cropping_system || c.croppingSystem,
                location: c.location || c.district || c.sector || 'Not specified',
                status: c.status || 'active',
                vslaGroup: `VSLA-${c.name}`,
                agronomist_name: c.agronomist_name,
                boundary_coordinates: c.boundary_coordinates,
                farm_boundaries: c.farm_boundaries,
                boundary_color: c.boundary_color,
                performance: c.performance || {
                    yieldIncrease: 0,
                    repaymentRate: 0,
                    attendanceRate: 0,
                    avgIncome: 0
                }
            }));
            setCohorts(formattedData as Cohort[]);
        } catch (error) {
            console.error('Failed to fetch cohorts:', error);
            setCohorts([]);
        } finally {
            setLoading(false);
        }
    };

    const loadFallbackDetails = (id: number) => {
        setCohortMetrics({
            yield: { current: 145, baseline: 113, target: 147 },
            repayment: { rate: 92, status: 'Healthy' },
            attendance: { rate: 88, sessions: 12 },
            retention: { rate: 94, active_farmers: 24, total_farmers: 25 },
            area: { hectares: 2.5, acres: 6.18 },
            financials: { revenue: 210000, costs: 35000, net: 175000 },
            social: { leadFarmers: 5, peerMentors: 22 },
            kpis: {
                yieldCurrent: 145,
                repaymentRate: 92,
                retentionRate: 94,
                totalAreaHectares: 2.5,
                totalAreaAcres: 6.18
            },
            dashboard: { kpis: { input_cost_recovery_rate: 78 } },
            traceability: { totals: { input_cost: 56000, sales_revenue: 210000, repayments: 43500 } }
        });
        setCohortFarmers([]);
    };

    const fetchCohortDetails = async (id: number) => {
        try {
            const [metrics, dashboard, traceability, farmers] = await Promise.all([
                apiGet(`/api/cohorts/${id}/metrics`),
                apiGet(`/api/cohorts/${id}/dashboard`),
                apiGet(`/api/cohorts/${id}/traceability`),
                apiGet(`/api/cohorts/${id}/farmers`)
            ]);
            setCohortMetrics({
                ...(metrics as any),
                dashboard,
                traceability
            });
            setCohortFarmers(farmers as any[]);
        } catch (error) {
            console.error('Failed to fetch cohort details:', error);
            setCohortMetrics(null);
            setCohortFarmers([]);
        }
    };

    const fetchTabData = async (tabId: string, cohortId: number) => {
        if (!cohortId) return;
        setTabLoading(tabId);
        
        try {
            switch (tabId) {
                case 'agronomy':
                    const agronomy = await apiGet(`/api/cohorts/${cohortId}/agronomy`);
                    setAgronomyData(agronomy);
                    break;
                case 'financials':
                    const financial = await apiGet(`/api/cohorts/${cohortId}/financials`);
                    setFinancialData(financial);
                    break;
                case 'training':
                    const training = await apiGet(`/api/cohorts/${cohortId}/training`);
                    setTrainingData(training);
                    break;
                case 'logistics':
                    const logistics = await apiGet(`/api/cohorts/${cohortId}/logistics`);
                    setLogisticsData(logistics);
                    break;
            }
        } catch (error) {
            console.error(`Failed to fetch ${tabId} data:`, error);
        } finally {
            setTabLoading(null);
        }
    };
 
    const handleDeleteCohort = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this cohort?')) return;

        try {
            await apiDelete(`/api/cohorts/${id}`);
            // Immediately update state without refetching
            setCohorts(cohorts.filter(c => c.id !== id));
            if (selectedCohort?.id === id) setSelectedCohort(null);
            alert('Cohort deleted successfully!');
        } catch (error) {
            console.error('Delete cohort error:', error);
            alert('Failed to delete cohort');
        }
    };

    const handleDeleteFarmer = async (farmerId: number) => {
        if (!window.confirm('Are you sure you want to remove this farmer from the cohort?')) return;

        try {
            await apiDelete(`/api/farmers/${farmerId}`);
            if (selectedCohort) {
                fetchCohortDetails(selectedCohort.id);
            }
        } catch (error) {
            alert('Failed to remove farmer');
        }
    };

    const handleCreateCohort = async (payload: any) => {
        try {
            await apiPost('/api/cohorts', payload);
            await fetchCohorts();
            setIsCreateModalOpen(false);
            alert('Cohort created successfully!');
        } catch (error) {
            alert('Failed to create cohort. Please try again.');
            throw error;
        }
    };

    const totalCohorts = cohorts.length;
    const totalFarmers = cohorts.reduce((sum, c) => sum + c.farmerCount, 0);

    const kpiStats = React.useMemo(() => {
        if (cohorts.length === 0) return {
            totalAreaHectares: 0,
            totalAreaAcres: 0,
            avgRetention: 0,
            activeFarmers: 0,
            warningCohorts: 0,
            avgYield: 0,
            repaymentRate: 0,
            trainingAttendance: 0,
        };

        const total = cohorts.length;
        const totalAreaHectares = cohorts.reduce((acc, c) => acc + (Number(c.areaHa) || 0), 0);
        const totalAreaAcres = cohorts.reduce((acc, c) => acc + (Number(c.areaAcres) || (Number(c.areaHa) || 0) * 2.47105), 0);
        const activeFarmers = cohorts.reduce((acc, c) => acc + (Number(c.activeFarmerCount) || Number(c.farmerCount) || 0), 0);
        const avgRetention = Math.round(
            cohorts.reduce((acc, c) => {
                if (typeof c.retentionRate === 'number') return acc + c.retentionRate;
                if ((c.farmerCount || 0) > 0) {
                    const inferredActive = Number(c.activeFarmerCount ?? c.farmerCount);
                    return acc + ((inferredActive / c.farmerCount) * 100);
                }
                return acc;
            }, 0) / total
        );

        return {
            totalAreaHectares: Number(totalAreaHectares.toFixed(2)),
            totalAreaAcres: Number(totalAreaAcres.toFixed(2)),
            avgRetention,
            activeFarmers,
            warningCohorts: cohorts.filter(c => c.status !== 'Active').length,
            avgYield: Math.round(cohorts.reduce((acc, c) => acc + (c.performance?.yieldIncrease || 0), 0) / total),
            repaymentRate: Math.round(cohorts.reduce((acc, c) => acc + (c.performance?.repaymentRate || 0), 0) / total),
            trainingAttendance: Math.round(cohorts.reduce((acc, c) => acc + (c.performance?.attendanceRate || 0), 0) / total),
        };
    }, [cohorts]);

    const {
        totalAreaHectares,
        totalAreaAcres,
        avgRetention,
        activeFarmers,
        warningCohorts,
        avgYield,
        repaymentRate,
        trainingAttendance
    } = kpiStats;

    const filteredCohorts = cohorts.filter(c => {
        if (systemFilter !== 'all' && c.croppingSystem !== systemFilter) return false;
        if (statusFilter !== 'all' && c.status !== statusFilter) return false;
        if (locationFilter !== 'all' && c.location !== locationFilter) return false;
        if (retentionFilter === 'high' && (c.retentionRate || 0) < 90) return false;
        if (retentionFilter === 'medium' && ((c.retentionRate || 0) < 70 || (c.retentionRate || 0) >= 90)) return false;
        if (retentionFilter === 'low' && (c.retentionRate || 0) >= 70) return false;
        return true;
    }).sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
            case 'farmers': aVal = a.farmerCount; bVal = b.farmerCount; break;
            case 'area': aVal = a.areaHa; bVal = b.areaHa; break;
            case 'retention': aVal = a.retentionRate || 0; bVal = b.retentionRate || 0; break;
            default: aVal = a.name; bVal = b.name;
        }
        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
    });

    const columns = [
        { key: 'name', label: 'Name', sortable: true, render: (v: string) => <span className="font-bold text-gray-900">{v}</span> },
        {
            key: 'croppingSystem',
            label: 'System',
            render: (v: string) => (
                <span className={`px-2 py-1 rounded text-xs font-medium ${v === 'Avocado' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                    {v}
                </span>
            )
        },
        { key: 'location', label: 'Location' },
        {
            key: 'farmerCount',
            label: 'Farmers',
            render: (v: number, row: Cohort) => (
                <div className="flex items-center">
                    <UserGroupIcon className="w-4 h-4 mr-1 text-gray-400" />
                    <span>{row.activeFarmerCount || v}/{v}</span>
                </div>
            )
        },
        {
            key: 'areaHa',
            label: 'Land Area',
            render: (_: number, row: Cohort) => (
                <div>
                    <p className="font-semibold text-slate-900">{Number(row.areaHa || 0).toFixed(2)} ha</p>
                    <p className="text-xs text-slate-500">({Number(row.areaAcres || 0).toFixed(2)} acres)</p>
                </div>
            )
        },
        {
            key: 'retentionRate',
            label: 'Retention',
            render: (v: number) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${Number(v || 0) >= 80 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {Number(v || 0).toFixed(1)}%
                </span>
            )
        },
        {
            key: 'status',
            label: 'Health',
            render: (v: string) => <StatusBadge status={v} />
        },
        {
            key: 'actions',
            label: '',
            render: (_: any, row: Cohort) => (
                <div className="flex items-center space-x-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); setSelectedCohort(row); }}
                        className="text-brand-blue-600 hover:text-brand-blue-800 text-sm font-medium hover:underline"
                    >
                        View Details
                    </button>
                    {canManageCohorts && (
                        <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteCohort(row.id); }}
                            className="text-red-400 hover:text-red-600 p-1"
                            title="Delete Cohort"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    const tabs = [
        { id: 'overview', label: 'Overview', icon: ChartBarIcon },
        { id: 'farmers', label: 'Farmers', icon: UserGroupIcon },
        { id: 'farm-map', label: 'Farm Map', icon: MapIcon },
        { id: 'agronomy', label: 'Agronomy', icon: MapIcon },
        { id: 'financials', label: 'Financials', icon: CurrencyDollarIcon },
        { id: 'training', label: 'Training', icon: AcademicCapIcon },
        { id: 'logistics', label: 'Logistics', icon: TruckIcon },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Enhanced Header */}
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                Cohort Command Center
                            </h1>
                            <p className="text-slate-600 mt-1">
                                {totalCohorts} Cohorts • {totalFarmers} Farmers • {totalAreaAcres.toFixed(2)} acres ({totalAreaHectares.toFixed(2)} ha)
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <ExportButton onExport={() => { }} label="Export Data" />
                            {canManageCohorts && (
                                <button
                                    onClick={() => setIsCreateModalOpen(true)}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 transition-all font-medium"
                                >
                                    <PlusIcon className="w-5 h-5 mr-2" />
                                    Create Cohort
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Enhanced KPI Cards with Retention */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-xs">Total Farmers</p>
                                    <p className="text-xl font-bold">{activeFarmers}/{totalFarmers}</p>
                                </div>
                                <UserGroupIcon className="w-6 h-6 text-blue-200" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-emerald-100 text-xs">Retention Rate</p>
                                    <p className="text-xl font-bold">{avgRetention}%</p>
                                </div>
                                <ChartBarIcon className="w-6 h-6 text-emerald-200" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-purple-100 text-xs">Repayment</p>
                                    <p className="text-xl font-bold">{repaymentRate}%</p>
                                </div>
                                <BanknotesIcon className="w-6 h-6 text-purple-200" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-orange-100 text-xs">Attendance</p>
                                    <p className="text-xl font-bold">{trainingAttendance}%</p>
                                </div>
                                <AcademicCapIcon className="w-6 h-6 text-orange-200" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-xs">Land Area</p>
                                    <p className="text-xl font-bold">{totalAreaAcres.toFixed(1)}</p>
                                    <p className="text-xs text-green-200">acres</p>
                                </div>
                                <MapIcon className="w-6 h-6 text-green-200" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-100 text-xs">At Risk</p>
                                    <p className="text-xl font-bold">{warningCohorts}</p>
                                </div>
                                <ScaleIcon className="w-6 h-6 text-red-200" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Filters */}
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Cropping System</label>
                                <select
                                    value={systemFilter}
                                    onChange={(e) => setSystemFilter(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                >
                                    <option value="all">All Systems</option>
                                    {Array.from(new Set(cohorts.map(c => c.croppingSystem))).map(sys => (
                                        <option key={sys} value={sys}>{sys}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Location</label>
                                <select
                                    value={locationFilter}
                                    onChange={(e) => setLocationFilter(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                >
                                    <option value="all">All Locations</option>
                                    {Array.from(new Set(cohorts.map(c => c.location))).map(loc => (
                                        <option key={loc} value={loc}>{loc}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Health Status</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="Active">Active</option>
                                    <option value="Warning">Warning</option>
                                    <option value="At Risk">At Risk</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Retention Rate</label>
                                <select
                                    value={retentionFilter}
                                    onChange={(e) => setRetentionFilter(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                >
                                    <option value="all">All Rates</option>
                                    <option value="high">High (≥90%)</option>
                                    <option value="medium">Medium (70-89%)</option>
                                    <option value="low">Low (&lt;70%)</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => {
                                    setSystemFilter('all');
                                    setStatusFilter('all');
                                    setLocationFilter('all');
                                    setRetentionFilter('all');
                                }}
                                className="px-6 py-2.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-all font-medium shadow-lg"
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Enhanced Table */}
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                    <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900">Cohort Health Matrix</h3>
                                <p className="text-sm text-slate-600 mt-1">{filteredCohorts.length} of {cohorts.length} cohorts displayed</p>
                            </div>
                            <div className="flex gap-2">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value as any)}
                                    className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="name">Sort by Name</option>
                                    <option value="farmers">Sort by Farmers</option>
                                    <option value="area">Sort by Area</option>
                                    <option value="retention">Sort by Retention</option>
                                </select>
                                <button
                                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                    className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                                >
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                </button>
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-slate-500 font-medium">Loading cohort data...</p>
                        </div>
                    ) : filteredCohorts.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="text-slate-400 mb-4">
                                <UserGroupIcon className="w-16 h-16 mx-auto" />
                            </div>
                            <p className="text-slate-600 font-medium">No cohorts match your filters</p>
                            <button
                                onClick={() => {
                                    setSystemFilter('all');
                                    setStatusFilter('all');
                                    setLocationFilter('all');
                                    setRetentionFilter('all');
                                }}
                                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Clear all filters
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-slate-100 border-b border-slate-200">
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Cohort Name</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">System</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Location</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Farmers</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-700 uppercase tracking-wider">Land Area</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Retention</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Health</th>
                                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-700 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {filteredCohorts.map((cohort, idx) => (
                                        <tr
                                            key={cohort.id}
                                            onClick={() => setSelectedCohort(cohort)}
                                            className={`hover:bg-blue-50 transition-colors cursor-pointer ${
                                                idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                                            }`}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold mr-3">
                                                        {cohort.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-slate-900">{cohort.name}</p>
                                                        <p className="text-xs text-slate-500">{cohort.vslaGroup}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                                                    cohort.croppingSystem === 'Avocado' ? 'bg-green-100 text-green-800' :
                                                    cohort.croppingSystem === 'Macadamia' ? 'bg-orange-100 text-orange-800' :
                                                    'bg-blue-100 text-blue-800'
                                                }`}>
                                                    {cohort.croppingSystem}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center text-slate-700">
                                                    <MapIcon className="w-4 h-4 mr-2 text-slate-400" />
                                                    <span className="font-medium">{cohort.location}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="inline-flex flex-col items-center">
                                                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-1">
                                                        <span className="text-lg font-bold text-blue-700">{cohort.activeFarmerCount || cohort.farmerCount}</span>
                                                    </div>
                                                    <span className="text-xs text-slate-500">of {cohort.farmerCount}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div>
                                                    <p className="text-lg font-bold text-slate-900">{Number(cohort.areaHa || 0).toFixed(1)}</p>
                                                    <p className="text-xs text-slate-500">ha</p>
                                                    <p className="text-xs text-slate-400">({Number(cohort.areaAcres || 0).toFixed(1)} acres)</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="inline-flex flex-col items-center">
                                                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-1 ${
                                                        (cohort.retentionRate || 0) >= 90 ? 'bg-green-100' :
                                                        (cohort.retentionRate || 0) >= 70 ? 'bg-yellow-100' : 'bg-red-100'
                                                    }`}>
                                                        <span className={`text-xl font-bold ${
                                                            (cohort.retentionRate || 0) >= 90 ? 'text-green-700' :
                                                            (cohort.retentionRate || 0) >= 70 ? 'text-yellow-700' : 'text-red-700'
                                                        }`}>
                                                            {Number(cohort.retentionRate || 0).toFixed(0)}%
                                                        </span>
                                                    </div>
                                                    <span className="text-xs text-slate-500 font-medium">
                                                        {(cohort.retentionRate || 0) >= 90 ? 'Excellent' :
                                                         (cohort.retentionRate || 0) >= 70 ? 'Good' : 'Needs Attention'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <StatusBadge status={cohort.status} />
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setSelectedCohort(cohort); }}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-medium shadow-sm"
                                                    >
                                                        View
                                                    </button>
                                                    {canManageCohorts && (
                                                        <>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setEditingCohort(cohort); }}
                                                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-medium shadow-sm"
                                                                title="Edit Cohort"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteCohort(cohort.id); }}
                                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                                title="Delete Cohort"
                                                            >
                                                                <TrashIcon className="w-5 h-5" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Enhanced Detail Modal */}
                {selectedCohort && (
                    <ModalLayout
                        isOpen={!!selectedCohort}
                        onClose={() => setSelectedCohort(null)}
                        title={`${selectedCohort.name} - Comprehensive View`}
                        size="wide"
                    >
                        <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 to-blue-50">
                            {/* Enhanced Tab Navigation */}
                            <div className="bg-white border-b border-gray-200 px-6 py-4">
                                <div className="flex flex-wrap gap-2">
                                    {tabs.map((tab) => {
                                        const Icon = tab.icon;
                                        const isActive = activeTab === tab.id;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => {
                                                    setActiveTab(tab.id as any);
                                                    if (['agronomy', 'financials', 'training', 'logistics'].includes(tab.id)) {
                                                        fetchTabData(tab.id, selectedCohort.id);
                                                    }
                                                }}
                                                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all ${isActive
                                                    ? 'bg-blue-600 text-white shadow-lg'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    }`}
                                            >
                                                <Icon className="w-4 h-4 mr-2" />
                                                {tab.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Enhanced Content Area */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {activeTab === 'overview' && cohortMetrics && (
                                    <div className="space-y-6">
                                        {/* Key Metrics */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-slate-500 text-sm">Farmer Retention</p>
                                                        <p className="text-2xl font-bold text-slate-900">{cohortMetrics?.retention?.rate || selectedCohort.retentionRate || 0}%</p>
                                                        <p className="text-xs text-slate-500">{cohortMetrics?.retention?.active_farmers || selectedCohort.activeFarmerCount}/{cohortMetrics?.retention?.total_farmers || selectedCohort.farmerCount} active</p>
                                                    </div>
                                                    <UserGroupIcon className="w-8 h-8 text-blue-500" />
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-slate-500 text-sm">Yield Performance</p>
                                                        <p className="text-2xl font-bold text-slate-900">{cohortMetrics?.yield?.current || 145} kg/ha</p>
                                                        <p className="text-xs text-green-600">+{selectedCohort.performance.yieldIncrease}% vs baseline</p>
                                                    </div>
                                                    <ChartBarIcon className="w-8 h-8 text-green-500" />
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-slate-500 text-sm">Repayment Rate</p>
                                                        <p className="text-2xl font-bold text-slate-900">{cohortMetrics?.repayment?.rate || selectedCohort.performance.repaymentRate}%</p>
                                                        <p className="text-xs text-slate-500">VSLA health</p>
                                                    </div>
                                                    <BanknotesIcon className="w-8 h-8 text-purple-500" />
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-slate-500 text-sm">Land Coverage</p>
                                                        <p className="text-2xl font-bold text-slate-900">{selectedCohort.areaHa?.toFixed(1) || 0}</p>
                                                        <p className="text-xs text-slate-500">ha ({(selectedCohort.areaHa * 2.47105)?.toFixed(1) || 0} acres)</p>
                                                    </div>
                                                    <MapIcon className="w-8 h-8 text-orange-500" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Map and Actions */}
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                            <div className="lg:col-span-2">
                                                <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                                                    <div className="h-96">
                                                        <MapboxMiniMap
                                                            key={`overview-${selectedCohort.id}`}
                                                            center={(() => {
                                                                const cohortBoundary = (selectedCohort as any).boundary_coordinates;
                                                                if (cohortBoundary && Array.isArray(cohortBoundary) && cohortBoundary.length > 0) {
                                                                    return [cohortBoundary[0][0] || cohortBoundary[0].lat, cohortBoundary[0][1] || cohortBoundary[0].lng] as [number, number];
                                                                }
                                                                const farmerWithBoundary = cohortFarmers.find((f: any) => f.plot_boundary);
                                                                if (farmerWithBoundary) {
                                                                    const boundary = typeof farmerWithBoundary.plot_boundary === 'string' 
                                                                        ? JSON.parse(farmerWithBoundary.plot_boundary) 
                                                                        : farmerWithBoundary.plot_boundary;
                                                                    if (boundary && boundary.length > 0) return [boundary[0][0] || boundary[0].lat, boundary[0][1] || boundary[0].lng] as [number, number];
                                                                }
                                                                return [-1.9441, 30.0619] as [number, number];
                                                            })()}
                                                            zoom={13}
                                                            basemap="streets"
                                                            style={{ height: '100%', width: '100%' }}
                                                            polygons={(() => {
                                                                const result: any[] = [];
                                                                const cohortBoundary = (selectedCohort as any).boundary_coordinates;
                                                                if (cohortBoundary) {
                                                                    let coords = cohortBoundary;
                                                                    if (typeof cohortBoundary === 'string') { try { coords = JSON.parse(cohortBoundary); } catch { return []; } }
                                                                    if (Array.isArray(coords) && coords.length >= 3) {
                                                                        result.push({ id: 'cohort', coordinates: coords.map((c: any) => [c[0] || c.lat, c[1] || c.lng]), color: (selectedCohort as any).boundary_color || '#3B82F6', label: selectedCohort.name });
                                                                    }
                                                                }
                                                                cohortFarmers.forEach((farmer: any) => {
                                                                    const boundary = farmer.plot_boundary ? (typeof farmer.plot_boundary === 'string' ? JSON.parse(farmer.plot_boundary) : farmer.plot_boundary) : null;
                                                                    if (boundary && Array.isArray(boundary) && boundary.length >= 3) {
                                                                        result.push({ id: `farmer-${farmer.id}`, coordinates: boundary.map((c: any) => [c[0] || c.lat, c[1] || c.lng]), color: '#10B981', label: farmer.full_name });
                                                                    }
                                                                });
                                                                return result;
                                                            })()}
                                                            fitBounds
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-4">
                                                <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                    <h4 className="font-semibold text-slate-900 mb-4">Quick Actions</h4>
                                                    <div className="space-y-3">
                                                        <button className="w-full text-left p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                                            <p className="font-medium text-blue-900">Schedule Training</p>
                                                            <p className="text-xs text-blue-600">Next session planning</p>
                                                        </button>
                                                        <button className="w-full text-left p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                                                            <p className="font-medium text-green-900">Review Financials</p>
                                                            <p className="text-xs text-green-600">VSLA & repayments</p>
                                                        </button>
                                                        <button className="w-full text-left p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                                                            <p className="font-medium text-orange-900">Export Report</p>
                                                            <p className="text-xs text-orange-600">Generate summary</p>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'farmers' && (
                                    <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="text-xl font-semibold text-slate-900">Farmer Roster ({cohortFarmers.length})</h3>
                                            <AssignFarmerButton cohortId={selectedCohort.id} onAssignmentUpdate={() => fetchCohortDetails(selectedCohort.id)} />
                                        </div>
                                        <DataTable
                                            columns={[
                                                {
                                                    key: 'photo_url',
                                                    label: '',
                                                    render: (v: string, row: any) => (
                                                        <img
                                                            src={v || 'https://via.placeholder.com/40'}
                                                            alt={row.full_name}
                                                            className="w-10 h-10 rounded-full object-cover border border-gray-200"
                                                            onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40?text=User'; }}
                                                        />
                                                    )
                                                },
                                                { key: 'full_name', label: 'Name', sortable: true, render: (v: string) => <span className="font-medium text-gray-900">{v}</span> },
                                                {
                                                    key: 'household_type',
                                                    label: 'Type',
                                                    render: (v: string) => (
                                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                                            {v?.replace('_', ' ')}
                                                        </span>
                                                    )
                                                },
                                                { key: 'phone_number', label: 'Phone' },
                                                {
                                                    key: 'plot_size_ha',
                                                    label: 'Plot Size',
                                                    render: (_: number, row: any) => `${Number(row.plot_size_ha || 0).toFixed(2)} ha`
                                                },
                                                {
                                                    key: 'actions',
                                                    label: 'Actions',
                                                    render: (_: any, row: any) => (
                                                        <button
                                                            onClick={() => handleDeleteFarmer(row.id)}
                                                            className="text-red-500 hover:text-red-700 p-1"
                                                            title="Remove from cohort"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    )
                                                }
                                            ]}
                                            data={cohortFarmers}
                                        />
                                    </div>
                                )}

                                {activeTab === 'farm-map' && (
                                    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                                        <div className="h-[600px]">
                                            <MapboxMiniMap
                                                key={`farm-map-${selectedCohort.id}`}
                                                center={(() => {
                                                    const cohortBoundary = (selectedCohort as any).boundary_coordinates;
                                                    if (cohortBoundary && Array.isArray(cohortBoundary) && cohortBoundary.length > 0) {
                                                        return [cohortBoundary[0][0] || cohortBoundary[0].lat, cohortBoundary[0][1] || cohortBoundary[0].lng] as [number, number];
                                                    }
                                                    const farmerWithBoundary = cohortFarmers.find((f: any) => f.plot_boundary);
                                                    if (farmerWithBoundary) {
                                                        const boundary = typeof farmerWithBoundary.plot_boundary === 'string' 
                                                            ? JSON.parse(farmerWithBoundary.plot_boundary) 
                                                            : farmerWithBoundary.plot_boundary;
                                                        if (boundary && boundary.length > 0) return [boundary[0][0] || boundary[0].lat, boundary[0][1] || boundary[0].lng] as [number, number];
                                                    }
                                                    return [-1.9441, 30.0619] as [number, number];
                                                })()}
                                                zoom={14}
                                                basemap="satellite"
                                                style={{ height: '100%', width: '100%' }}
                                                fitBounds
                                                polygons={(() => {
                                                    const result: any[] = [];
                                                    const cohortBoundary = (selectedCohort as any).boundary_coordinates;
                                                    if (cohortBoundary) {
                                                        let coords = cohortBoundary;
                                                        if (typeof cohortBoundary === 'string') { try { coords = JSON.parse(cohortBoundary); } catch { return []; } }
                                                        if (Array.isArray(coords) && coords.length >= 3) {
                                                            result.push({ id: 'cohort-boundary', coordinates: coords.map((c: any) => [c[0] || c.lat, c[1] || c.lng]), color: (selectedCohort as any).boundary_color || '#3B82F6', label: selectedCohort.name });
                                                        }
                                                    }
                                                    cohortFarmers.forEach((farmer: any) => {
                                                        const boundary = farmer.plot_boundary ? (typeof farmer.plot_boundary === 'string' ? JSON.parse(farmer.plot_boundary) : farmer.plot_boundary) : null;
                                                        if (boundary && Array.isArray(boundary) && boundary.length >= 3) {
                                                            result.push({ id: `farmer-${farmer.id}`, coordinates: boundary.map((c: any) => [c[0] || c.lat, c[1] || c.lng]), color: '#10B981', fillColor: '#10B981', fillOpacity: 0.3, label: farmer.full_name });
                                                        }
                                                    });
                                                    return result;
                                                })()}
                                            />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'agronomy' && (
                                    <div className="space-y-6">
                                        {tabLoading === 'agronomy' ? (
                                            <div className="text-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                                <p className="text-slate-600 mt-2">Loading agronomy data...</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Assign Agronomist Section - Only for Project Managers */}
                                                {user?.role === 'project_manager' && (
                                                    <AssignAgronomistSection 
                                                        cohortId={selectedCohort.id}
                                                        currentAgronomist={selectedCohort.agronomist_name}
                                                        onAssignmentUpdate={() => {
                                                            fetchCohorts();
                                                            fetchCohortDetails(selectedCohort.id);
                                                        }}
                                                    />
                                                )}
                                                
                                                {agronomyData ? (
                                                    <>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                                <h4 className="font-semibold text-slate-900 mb-4">Crop Health</h4>
                                                                <div className="space-y-3">
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Disease Incidence</span>
                                                                        <span className={`font-medium ${agronomyData.disease_rate < 10 ? 'text-green-600' : 'text-red-600'}`}>
                                                                            {agronomyData.disease_status || 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Pest Pressure</span>
                                                                        <span className="font-medium text-yellow-600">{agronomyData.pest_status || 'N/A'}</span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Soil pH</span>
                                                                        <span className="font-medium text-green-600">{agronomyData.soil_ph || 'N/A'}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                                <h4 className="font-semibold text-slate-900 mb-4">Input Usage</h4>
                                                                <div className="space-y-3">
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Fertilizer</span>
                                                                        <span className="font-medium">{agronomyData.fertilizer_usage || '0'}% Applied</span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Seeds</span>
                                                                        <span className="font-medium">{agronomyData.seed_distribution || '0'}% Distributed</span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Pesticides</span>
                                                                        <span className="font-medium">{agronomyData.pesticide_usage || '0'}% Used</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                                <h4 className="font-semibold text-slate-900 mb-4">Season Progress</h4>
                                                                <div className="space-y-3">
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Planting</span>
                                                                        <span className={`font-medium ${agronomyData.planting_status === 'Complete' ? 'text-green-600' : 'text-blue-600'}`}>
                                                                            {agronomyData.planting_status || 'Pending'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Weeding</span>
                                                                        <span className={`font-medium ${agronomyData.weeding_status === 'Complete' ? 'text-green-600' : 'text-blue-600'}`}>
                                                                            {agronomyData.weeding_status || 'Pending'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between">
                                                                        <span className="text-slate-600">Harvest</span>
                                                                        <span className={`font-medium ${agronomyData.harvest_status === 'Complete' ? 'text-green-600' : 'text-slate-400'}`}>
                                                                            {agronomyData.harvest_status || 'Pending'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {agronomyData.field_visits && (
                                                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                                <h4 className="font-semibold text-slate-900 mb-4">Recent Field Visits</h4>
                                                                <div className="space-y-3">
                                                                    {agronomyData.field_visits.map((visit: any, index: number) => (
                                                                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                                                            <div>
                                                                                <p className="font-medium">{visit.activity || 'Field Visit'}</p>
                                                                                <p className="text-sm text-slate-600">{visit.notes || ''} • {visit.date || 'Recent'}</p>
                                                                            </div>
                                                                            <span className={`px-2 py-1 rounded text-xs ${
                                                                                visit.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                                            }`}>
                                                                                {visit.status || 'Pending'}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="text-center py-8 text-slate-500">
                                                        <p>No agronomy data available for this cohort</p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'financials' && (
                                    <div className="space-y-6">
                                        {tabLoading === 'financials' ? (
                                            <div className="text-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                                <p className="text-slate-600 mt-2">Loading financial data...</p>
                                            </div>
                                        ) : financialData ? (
                                            <>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">Total Revenue</h4>
                                                        <p className="text-2xl font-bold text-green-600">{formatCurrency(financialData.total_revenue || 0)}</p>
                                                        <p className="text-xs text-slate-500">This season</p>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">Input Costs</h4>
                                                        <p className="text-2xl font-bold text-red-600">{formatCurrency(financialData.input_costs || 0)}</p>
                                                        <p className="text-xs text-slate-500">Seeds, fertilizer, etc.</p>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">Net Profit</h4>
                                                        <p className="text-2xl font-bold text-blue-600">{formatCurrency((financialData.total_revenue || 0) - (financialData.input_costs || 0))}</p>
                                                        <p className="text-xs text-slate-500">After all costs</p>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">VSLA Savings</h4>
                                                        <p className="text-2xl font-bold text-purple-600">{formatCurrency(financialData.vsla_savings || 0)}</p>
                                                        <p className="text-xs text-slate-500">Group savings</p>
                                                    </div>
                                                </div>
                                                <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                    <h4 className="font-semibold text-slate-900 mb-4">Payment Status</h4>
                                                    <div className="space-y-3">
                                                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                                            <div>
                                                                <p className="font-medium text-green-900">Input Loan Repayment</p>
                                                                <p className="text-sm text-green-600">{financialData.repayment_rate || 0}% completion rate</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="font-bold text-green-900">{formatCurrency(financialData.repaid_amount || 0)}</p>
                                                                <p className="text-xs text-green-600">of {formatCurrency(financialData.loan_amount || 0)}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                                                            <div>
                                                                <p className="font-medium text-blue-900">VSLA Contributions</p>
                                                                <p className="text-sm text-blue-600">Weekly savings active</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="font-bold text-blue-900">{formatCurrency(financialData.vsla_contributions || 0)}</p>
                                                                <p className="text-xs text-blue-600">Total contributed</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center py-8 text-slate-500">
                                                <p>No financial data available for this cohort</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'training' && (
                                    <div className="space-y-6">
                                        {tabLoading === 'training' ? (
                                            <div className="text-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                                <p className="text-slate-600 mt-2">Loading training data...</p>
                                            </div>
                                        ) : trainingData ? (
                                            <>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="font-semibold text-slate-900 mb-4">Attendance</h4>
                                                        <div className="text-center">
                                                            <p className="text-3xl font-bold text-blue-600">{trainingData.avg_attendance_rate || 0}%</p>
                                                            <p className="text-slate-500">Average attendance</p>
                                                        </div>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="font-semibold text-slate-900 mb-4">Sessions</h4>
                                                        <div className="text-center">
                                                            <p className="text-3xl font-bold text-green-600">{trainingData.total_sessions || 0}</p>
                                                            <p className="text-slate-500">Completed this season</p>
                                                        </div>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="font-semibold text-slate-900 mb-4">Lead Farmers</h4>
                                                        <div className="text-center">
                                                            <p className="text-3xl font-bold text-purple-600">{trainingData.champion_count || 0}</p>
                                                            <p className="text-slate-500">Active peer mentors</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                {trainingData.sessions && trainingData.sessions.length > 0 && (
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="font-semibold text-slate-900 mb-4">Training Sessions</h4>
                                                        <div className="space-y-3">
                                                            {trainingData.sessions.map((session: any, index: number) => (
                                                                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                                                    <div>
                                                                        <p className="font-medium">{session.topic || 'Training Session'}</p>
                                                                        <p className="text-sm text-slate-600">{session.attendees || 0} attendees • {session.date || 'Recent'}</p>
                                                                    </div>
                                                                    <span className={`px-2 py-1 rounded text-xs ${
                                                                        session.status === 'Completed' ? 'bg-green-100 text-green-800' : 
                                                                        session.status === 'Upcoming' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                                                                    }`}>
                                                                        {session.status || 'Pending'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-center py-8 text-slate-500">
                                                <p>No training data available for this cohort</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'logistics' && (
                                    <div className="space-y-6">
                                        {tabLoading === 'logistics' ? (
                                            <div className="text-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                                <p className="text-slate-600 mt-2">Loading logistics data...</p>
                                            </div>
                                        ) : logisticsData ? (
                                            <>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">Input Distribution</h4>
                                                        <p className="text-2xl font-bold text-green-600">{logisticsData.distribution_rate || 0}%</p>
                                                        <p className="text-xs text-slate-500">Seeds & fertilizer</p>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">Collection Points</h4>
                                                        <p className="text-2xl font-bold text-blue-600">{logisticsData.collection_points || 0}</p>
                                                        <p className="text-xs text-slate-500">Active locations</p>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">Transport Cost</h4>
                                                        <p className="text-2xl font-bold text-orange-600">{formatCurrency(logisticsData.transport_cost || 0)}</p>
                                                        <p className="text-xs text-slate-500">Per delivery</p>
                                                    </div>
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="text-slate-500 text-sm mb-2">Storage Capacity</h4>
                                                        <p className="text-2xl font-bold text-purple-600">{logisticsData.storage_usage || 0}%</p>
                                                        <p className="text-xs text-slate-500">Warehouse usage</p>
                                                    </div>
                                                </div>
                                                {logisticsData.deliveries && logisticsData.deliveries.length > 0 && (
                                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                                        <h4 className="font-semibold text-slate-900 mb-4">Delivery Schedule</h4>
                                                        <div className="space-y-3">
                                                            {logisticsData.deliveries.map((delivery: any, index: number) => (
                                                                <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                                                                    delivery.status === 'Completed' ? 'bg-green-50' : 'bg-blue-50'
                                                                }`}>
                                                                    <div>
                                                                        <p className={`font-medium ${
                                                                            delivery.status === 'Completed' ? 'text-green-900' : 'text-blue-900'
                                                                        }`}>{delivery.type || 'Delivery'}</p>
                                                                        <p className={`text-sm ${
                                                                            delivery.status === 'Completed' ? 'text-green-600' : 'text-blue-600'
                                                                        }`}>{delivery.route || 'Route not specified'}</p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <p className={`font-medium ${
                                                                            delivery.status === 'Completed' ? 'text-green-900' : 'text-blue-900'
                                                                        }`}>{delivery.status || 'Pending'}</p>
                                                                        <p className={`text-xs ${
                                                                            delivery.status === 'Completed' ? 'text-green-600' : 'text-blue-600'
                                                                        }`}>{delivery.date || 'Date TBD'}</p>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-center py-8 text-slate-500">
                                                <p>No logistics data available for this cohort</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ModalLayout>
                )}

                {/* Create Cohort Modal */}
                {isCreateModalOpen && (
                    <ModalLayout
                        isOpen={isCreateModalOpen}
                        onClose={() => setIsCreateModalOpen(false)}
                        title="Create New Cohort"
                        size="wide"
                    >
                        <CreateCohortForm
                            onSubmit={handleCreateCohort}
                            onCancel={() => setIsCreateModalOpen(false)}
                        />
                    </ModalLayout>
                )}

                {/* Edit Cohort Modal */}
                {editingCohort && (
                    <ModalLayout
                        isOpen={!!editingCohort}
                        onClose={() => setEditingCohort(null)}
                        title="Edit Cohort"
                        size="wide"
                    >
                        <CreateCohortForm
                            onSubmit={async (payload) => {
                                try {
                                    await apiPatch(`/api/cohorts/${editingCohort.id}`, payload);
                                    await fetchCohorts();
                                    setEditingCohort(null);
                                    alert('Cohort updated successfully!');
                                } catch (error: any) {
                                    alert(error.message || 'Failed to update cohort. Please try again.');
                                    throw error;
                                }
                            }}
                            onCancel={() => setEditingCohort(null)}
                            initialData={editingCohort}
                        />
                    </ModalLayout>
                )}

                {/* Create Modal remains the same but with enhanced styling */}
            </div>
        </div>
    );
};

export default CohortsPage;
